import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { CategoryBadge } from '@/components/CategoryBadge';
import { api, fileUrl } from '@/lib/api';
import { colorForCategory, textAccent } from '@/lib/categories';
import { useCountUp } from '@/hooks/useCountUp';
import {
    ArrowLeft, ExternalLink, Instagram, Linkedin, Twitter, Globe,
    Copy, Check, Share2, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

export default function RecipientDetail() {
    const { slug } = useParams();
    const [recipient, setRecipient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [captions, setCaptions] = useState({ linkedinCaption: '', twitterCaption: '' });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/recipients/${slug}`)
            .then(({ data }) => { if (!cancelled) setRecipient(data); })
            .catch(() => { if (!cancelled) setRecipient(false); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [slug]);

    // Fetch editable share captions (admin can change these anytime)
    useEffect(() => {
        api.get('/share-settings').then(({ data }) => setCaptions(data)).catch(() => {});
    }, []);

    const backendOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const shareUrl = `${backendOrigin}/api/share/${slug}`;
    const canonicalUrl = typeof window !== 'undefined' ? window.location.href : shareUrl;

    // Fill editable caption template variables
    const fillTemplate = (tpl) => {
        if (!tpl || !recipient) return '';
        const phaseShort = (recipient.awards || []).slice(0, 3).join(', ')
            + ((recipient.awards || []).length > 3 ? ' +more' : '');
        return tpl
            .replaceAll('{name}', `${recipient.firstName} ${recipient.lastName}`.trim())
            .replaceAll('{firstName}', recipient.firstName || '')
            .replaceAll('{company}', recipient.companyName || '')
            .replaceAll('{phases}', phaseShort)
            .replaceAll('{milestones}', String(recipient.totalMilestones ?? 0));
    };
    const linkedinText = fillTemplate(captions.linkedinCaption) ||
        `Congrats to ${recipient?.firstName || ''} ${recipient?.lastName || ''} — winner of the UnCloned® Brand Awards.`;
    const twitterText = fillTemplate(captions.twitterCaption) ||
        `Congrats to ${recipient?.firstName || ''} ${recipient?.lastName || ''} — winner of the UnCloned® Brand Awards.`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success('Link copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Could not copy link');
        }
    };

    const handleLinkedInShare = async (e) => {
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(`${linkedinText}\n\n${shareUrl}`);
            toast.success('Caption copied — paste it into your LinkedIn post');
        } catch { /* ignore */ }
        window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            '_blank', 'noopener,noreferrer',
        );
    };
    void canonicalUrl;

    if (loading) {
        return (
            <div className="App">
                <Navbar variant="public" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-40 flex items-center justify-center text-white/60">
                    <Loader2 className="animate-spin" size={22} /><span className="ml-3 uppercase tracking-[0.2em] text-xs">Loading</span>
                </div>
                <Footer />
            </div>
        );
    }

    if (!recipient) {
        return (
            <div className="App">
                <Navbar variant="public" />
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
                    <div className="font-display text-4xl font-black text-white mb-3">Not found</div>
                    <p className="text-white/60 mb-8">We couldn&apos;t find that recipient. They may have been removed.</p>
                    <Link to="/" data-testid="back-to-home-link" className="inline-flex items-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3">
                        <ArrowLeft size={16} /> Back to directory
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const primary = recipient.awards?.[0];
    const primaryColor = primary ? colorForCategory(primary) : '#DF1283';
    const primaryText = primary ? textAccent(primary) : '#DF1283';
    // Show a rendered preview to visitors; the raw PDF is not downloadable.
    const hasCert = !!recipient.hasCertificate;
    const certPreview = hasCert
        ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/certificate-preview/${recipient.slug}`
        : '';

    return (
        <div className="App">
            <Navbar variant="public" />

            {/* Header block */}
            <section
                className="relative border-b border-white/10 overflow-hidden"
                style={{
                    background: `radial-gradient(1200px 500px at 10% 0%, ${primaryColor}22, transparent 60%), #0a0a0a`,
                }}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16 md:pt-12 md:pb-24">
                    <Link
                        to="/"
                        data-testid="back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to directory
                    </Link>

                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        {/* Left: circular photo + badges */}
                        <div className="lg:col-span-5">
                            <div className="relative mx-auto max-w-md">
                                {/* accent glow */}
                                <div
                                    aria-hidden
                                    className="absolute inset-4 rounded-full opacity-40 blur-3xl"
                                    style={{ background: primaryColor }}
                                />
                                <div
                                    className="relative aspect-square rounded-full overflow-hidden bg-neutral-900"
                                    style={{ boxShadow: `0 0 0 6px ${primaryColor}` }}
                                >
                                    {recipient.photoUrl ? (
                                        <img
                                            src={fileUrl(recipient.photoUrl)}
                                            alt={`${recipient.firstName} ${recipient.lastName}`}
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div
                                            className="h-full w-full flex items-center justify-center font-display text-8xl font-black text-black"
                                            style={{ background: primaryColor }}
                                        >
                                            {recipient.firstName?.[0]}{recipient.lastName?.[0]}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div
                                data-testid="award-badges"
                                className="mt-8 flex flex-wrap gap-2 justify-center"
                            >
                                {(recipient.awards || []).map((a) => (
                                    <CategoryBadge key={a} name={a} />
                                ))}
                            </div>
                        </div>

                        {/* Right: content */}
                        <div className="lg:col-span-7">
                            <div className="text-xs uppercase tracking-[0.28em] text-un-lime font-semibold">
                                UnCloned<span className="text-un-berry">®</span> Brand Award Awardee
                            </div>
                            <h1
                                data-testid="recipient-name"
                                className="mt-3 font-display font-black uppercase tracking-tight leading-[0.95] text-white text-4xl sm:text-5xl md:text-6xl"
                            >
                                {recipient.firstName} <br className="hidden sm:block" />{recipient.lastName}
                            </h1>
                            {recipient.companyName && (
                                <div data-testid="recipient-company" className="mt-4 text-white/70 text-lg font-body">
                                    {recipient.companyName}
                                </div>
                            )}

                            {/* Milestones stat with count-up animation */}
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                {recipient.awardYear ? (
                                    <div
                                        data-testid="recipient-year-pill"
                                        className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-black/60 backdrop-blur-md px-5 py-2"
                                    >
                                        <span
                                            className="font-display font-black text-2xl tabular-nums text-un-lime"
                                        >
                                            {recipient.awardYear}
                                        </span>
                                        <span className="text-xs uppercase tracking-[0.22em] text-white/70">
                                            Cohort
                                        </span>
                                    </div>
                                ) : null}
                                <MilestonesStat
                                    total={recipient.totalMilestones ?? 0}
                                    slug={recipient.slug}
                                    accent={primaryText}
                                />
                            </div>

                            {recipient.bio && (
                                <div
                                    data-testid="recipient-bio"
                                    className="mt-8 text-white/85 leading-relaxed font-body text-base md:text-lg max-w-2xl bio-prose"
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            p: ({ node, ...props }) => <p className="mb-4 last:mb-0" {...props} />,
                                            a: ({ node, ...props }) => (
                                                <a
                                                    {...props}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-un-lime underline underline-offset-4 hover:text-white transition-colors"
                                                />
                                            ),
                                            strong: ({ node, ...props }) => <strong className="text-white font-bold" {...props} />,
                                            em: ({ node, ...props }) => <em className="italic text-white/95" {...props} />,
                                            ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
                                            ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
                                        }}
                                    >
                                        {recipient.bio}
                                    </ReactMarkdown>
                                </div>
                            )}

                            {/* Links */}
                            <div className="mt-8 flex flex-wrap gap-3">
                                {recipient.links?.website && (
                                    <ExternalLinkChip href={recipient.links.website} icon={Globe} label="Website" testid="link-website" />
                                )}
                                {recipient.links?.instagram && (
                                    <ExternalLinkChip href={recipient.links.instagram} icon={Instagram} label="Instagram" testid="link-instagram" />
                                )}
                                {recipient.links?.linkedin && (
                                    <ExternalLinkChip href={recipient.links.linkedin} icon={Linkedin} label="LinkedIn" testid="link-linkedin" />
                                )}
                                {recipient.links?.twitter && (
                                    <ExternalLinkChip href={recipient.links.twitter} icon={Twitter} label="X / Twitter" testid="link-twitter" />
                                )}
                            </div>

                            {/* Share bar hidden from public — admin still controls copy via /admin/share-settings. */}
                        </div>
                    </div>
                </div>
            </section>

            {/* Acceptance speech */}
            {recipient.hasAcceptanceSpeech && recipient.acceptanceSpeechEmbed?.trim() && (
                <section
                    data-testid="acceptance-speech-section"
                    className="bg-black border-b border-white/10"
                >
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
                        <div className="mb-8">
                            <div className="text-xs uppercase tracking-[0.28em] text-un-lime font-semibold">
                                In their words
                            </div>
                            <h2 className="mt-2 font-display text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                                Acceptance Speech
                            </h2>
                        </div>
                        <AcceptanceSpeechEmbed raw={recipient.acceptanceSpeechEmbed} />
                    </div>
                </section>
            )}

            {/* Certificate */}
            <section
                data-testid="certificate-section"
                className="bg-black border-b border-white/10"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <div className="text-xs uppercase tracking-[0.28em] text-un-water font-semibold">
                                Recognition
                            </div>
                            <h2 className="mt-2 font-display text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                                Certificate of Award
                            </h2>
                        </div>
                    </div>

                    {hasCert ? (
                        <div className="mx-auto max-w-4xl">
                            {/* 11" × 8.5" certificate frame — matches US Letter landscape exactly */}
                            <div
                                className="relative rounded-2xl overflow-hidden border border-white/15 bg-white shadow-[0_30px_80px_-20px_rgba(223,18,131,0.35)]"
                                style={{ aspectRatio: '11 / 8.5' }}
                            >
                                <img
                                    data-testid="certificate-preview"
                                    src={certPreview}
                                    alt="Certificate of Award"
                                    className="absolute inset-0 w-full h-full object-contain"
                                />
                            </div>
                            <p className="mt-4 text-center text-[11px] uppercase tracking-[0.24em] text-white/40">
                                11″ × 8.5″ landscape · reviewed & approved
                            </p>
                        </div>
                    ) : (
                        <div
                            data-testid="certificate-placeholder"
                            className="rounded-3xl border border-dashed border-white/20 p-10 md:p-16 text-center"
                            style={{ background: `linear-gradient(135deg, ${primaryColor}0F, #0a0a0a 60%)` }}
                        >
                            <div className="mx-auto max-w-md">
                                <div
                                    className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full"
                                    style={{ background: primaryColor, color: '#000' }}
                                >
                                    <FileText size={26} />
                                </div>
                                <div className="font-display text-2xl md:text-3xl font-black text-white uppercase tracking-tight">
                                    Certificate uploading soon
                                </div>
                                <p className="mt-3 text-white/60 font-body">
                                    A personalized UnCloned<span className="text-un-berry">®</span> Brand Award certificate will appear here once uploaded by the admin.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}

function AcceptanceSpeechEmbed({ raw }) {
    // Admins can paste either a full <iframe> embed snippet, or a plain video URL.
    // We accept both and render inside a responsive 16:9 frame.
    const html = React.useMemo(() => buildEmbedHtml(raw), [raw]);
    if (!html) {
        return (
            <div className="rounded-2xl border border-white/15 bg-neutral-950 p-6 text-sm text-white/60">
                Video will appear here once a valid embed code is added.
            </div>
        );
    }
    return (
        <div
            data-testid="acceptance-speech-player"
            className="relative mx-auto max-w-4xl rounded-2xl overflow-hidden border border-white/15 bg-black shadow-[0_30px_80px_-20px_rgba(15,255,226,0.28)]"
            style={{ aspectRatio: '16 / 9' }}
        >
            <div
                className="absolute inset-0 [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:border-0 [&_video]:absolute [&_video]:inset-0 [&_video]:h-full [&_video]:w-full"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}

function buildEmbedHtml(raw) {
    if (!raw) return '';
    const src = String(raw).trim();
    if (!src) return '';

    // If it already looks like markup, strip <script> tags for basic hygiene and use as-is.
    if (src.includes('<')) {
        return src.replace(/<script[\s\S]*?<\/script>/gi, '');
    }

    // Otherwise, try to build an embed from common video URLs.
    try {
        const u = new URL(src);
        const host = u.hostname.replace(/^www\./, '');

        // YouTube
        if (host === 'youtube.com' || host === 'm.youtube.com') {
            const id = u.searchParams.get('v');
            if (id) return youTubeIframe(id);
            const m = u.pathname.match(/\/(embed|shorts)\/([\w-]+)/);
            if (m) return youTubeIframe(m[2]);
        }
        if (host === 'youtu.be') {
            const id = u.pathname.slice(1);
            if (id) return youTubeIframe(id);
        }

        // Vimeo
        if (host === 'vimeo.com') {
            const id = u.pathname.split('/').filter(Boolean).pop();
            if (id && /^\d+$/.test(id)) {
                return `<iframe src="https://player.vimeo.com/video/${id}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Acceptance speech"></iframe>`;
            }
        }

        // Loom
        if (host === 'loom.com' || host === 'www.loom.com') {
            const m = u.pathname.match(/\/share\/([\w-]+)/);
            if (m) {
                return `<iframe src="https://www.loom.com/embed/${m[1]}" allowfullscreen title="Acceptance speech"></iframe>`;
            }
        }
    } catch { /* not a URL */ }

    return '';
}

function youTubeIframe(id) {
    return `<iframe src="https://www.youtube.com/embed/${encodeURIComponent(id)}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen title="Acceptance speech"></iframe>`;
}

function MilestonesStat({ total, slug, accent }) {
    // `slug` used as a trigger so the animation replays when you navigate between recipients.
    const value = useCountUp(total, { duration: 1400, trigger: slug });
    return (
        <div
            data-testid="milestones-stat"
            className="inline-flex items-center gap-4 rounded-full border border-white/15 bg-black/60 backdrop-blur-md pl-5 pr-6 py-2"
        >
            <span
                data-testid="milestones-count"
                className="font-display font-black text-2xl tabular-nums"
                style={{ color: accent }}
            >
                {value}
            </span>
            <span className="text-xs uppercase tracking-[0.22em] text-white/70">Milestones completed</span>
        </div>
    );
}

function ExternalLinkChip({ href, icon: Icon, label, testid }) {
    return (
        <a
            data-testid={testid}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:border-un-water hover:text-un-water transition-colors"
        >
            <Icon size={16} /> {label}
            <ExternalLink size={12} className="opacity-60" />
        </a>
    );
}

function ShareBtn({ href, children, aside, testid, onClick }) {
    return (
        <a
            data-testid={testid}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onClick}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-neutral-900 px-4 py-2 text-sm text-white hover:border-un-berry hover:text-un-berry transition-colors"
        >
            {children}
            {aside && <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 ml-1">{aside}</span>}
        </a>
    );
}
